import { useEffect, useRef, useState } from "react";
import { Box, Tabs, Tab, Paper, useTheme } from "@mui/material";
import CIS_img from "../../../../assets/baseVersion/CIS_img.svg";
import Spark_img from "../../../../assets/baseVersion/Details_Spark.svg";
import DISA_img from "../../../../assets/baseVersion/DISA.svg";
import Nist_img from "../../../../assets/baseVersion/Nist.svg";
import Hippa_img from "../../../../assets/baseVersion/hippa.svg";
import Pci_img from "../../../../assets/baseVersion/pcidss.svg";
import Mitre_img from "../../../../assets/baseVersion/mitre.svg";
import customTemplateLogo from "../../../../assets/custom_template_logo.svg";
import { useDispatch, useSelector } from "react-redux";
import { setButtonSelected, setCategorySelected } from "./TabSelection-slice";
import CIS_Dark from "../../../../assets/baseVersion/CIS_Dark.svg"
import Nist_Dark from "../../../../assets/baseVersion/Nist_Dark.svg";
import Mitre_Dark from "../../../../assets/baseVersion/Mitre_Dark.svg"
import Spark_Dark from "../../../../assets/baseVersion/Spark_Dark.svg"
import Hippa_Dark from "../../../../assets/baseVersion/Hippa_Dark.svg"
import { RootState } from "../../../../store/store";
import axiosInstance from "../../../../axios/axios";
import { SkeletonLoader } from "../../../../modules/dashboard/shared";
import { useParams } from "react-router-dom";

interface TabSelectionProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  module?: string
  id?: any
  parameterType?: string
  controlType?: string | string[]
}

const TabSelection: React.FC<TabSelectionProps> = ({ setLoading, module, id, parameterType, controlType }) => {
  const isTemplate =
    parameterType?.toLowerCase() === "template" ||
    parameterType?.toLowerCase() === "custom";
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabsData, setTabsData] = useState<{ label: string; icon: string; category: string }[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [isBenchVersions, setBenchVersions] = useState<any>()


  // Module comes from parent (e.g. ScanDetails useMemo); do not infer from URL.
  const resolvedModule =
    module === "on_premise" || module === "cloud" ? module : "";

  const on_Prem_Tab_Selected = useSelector(
    (state: RootState) => state.OnPrem_DetailsSelectedTab.selectedTab
  );
  const Selected_Rowid = useSelector(
    (state: any) => state.OnPrem_DetailsSelectedTab.row_id
  );

  const dispatch = useDispatch();
  // The clicked control's type (the row click stores it); read, not a dependency,
  // when the vendor list loads -- see the cloud case below.
  const selectedButton = useSelector(
    (state: { OnPrem_DetailsSelectedTab: { selectedButton: string } }) =>
      state.OnPrem_DetailsSelectedTab.selectedButton
  );
  const selectedButtonRef = useRef(selectedButton);
  selectedButtonRef.current = selectedButton;
  // const [loading, setLoading] = useState(false); // Add loading state

  const iconMap: Record<string, string> = {
    CIS: isDarkMode ? CIS_Dark : CIS_img,
    SPARK: isDarkMode ? Spark_Dark : Spark_img,
    DISA: DISA_img,
    NIST: isDarkMode ? Nist_Dark : Nist_img,
    HIPPA: isDarkMode ? Hippa_Dark : Hippa_img,
    "PCI DSS": Pci_img,
    MITRE: isDarkMode ? Mitre_Dark : Mitre_img,
  };

  const { scan_uuid: paramScanUuid } = useParams<{ scan_uuid: string }>();
  const { onprem_posture_id: reduxPostureId, scanType } = useSelector(
    (state: RootState) => state.MyScanDetails
  );
  
  const scan_uuid = scanType === "onprem_discovery_posture" && reduxPostureId 
    ? reduxPostureId 
    : paramScanUuid;

  useEffect(() => {
    if (isTemplate) {
      setTabsData([{ label: "Template", icon: customTemplateLogo }]);
      setIsReady(true);
      setIsLoading(false);
      setLoading(false);
      dispatch(setButtonSelected("template"));
      return;
    }

    const fetchData = async () => {
      if (!id || !resolvedModule) return;

      setIsLoading(true);
      setLoading(true);

      setIsReady(false);
      try {
        const category_name = "Benchmark";
        const response = await axiosInstance.get(
          `securityscan/vendor-device-versions/${id}?module=${resolvedModule}&category_name=${category_name}&scan_summary_id=${scan_uuid}`
        );
        const rawData = response?.data?.data;

        // Cloud: vendor-device-versions answers with a flat list of vendor names and no
        // control types, so there is nothing to parse (treating it as vendor -> model ->
        // versions threw, and no tab was ever built). The Benchmark tab is then the
        // clicked control's own type.
        if (Array.isArray(rawData)) {
          const clicked = selectedButtonRef.current;
          setBenchVersions(rawData.length > 0 && clicked ? [clicked.toUpperCase()] : []);
          return;
        }

        // Use a Set to store unique control_display_name values
        const controlDisplayNamesSet = new Set<string>();

        Object.values(rawData || {}).forEach((deviceObj: any) => {
          Object.values(deviceObj).forEach((versions: any) => {
            versions.forEach((item: any) => {
              if (item.control_display_name) {
                controlDisplayNamesSet.add(item.control_display_name);
              }
            });
          });
        });

        setBenchVersions(Array.from(controlDisplayNamesSet)); // Convert Set to array

      } catch (error) {
        console.error("Error fetching CIS bench versions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, resolvedModule, scan_uuid, setLoading, isTemplate, dispatch]);



  useEffect(() => {
    const fetchButtons = async () => {
      if (!Selected_Rowid || !resolvedModule || isTemplate) return;

      setIsLoading(true);
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `securityscan/compliance-list-myscans?parameter_name_id=${Selected_Rowid}&module=${resolvedModule}`
        );
        const categories = response?.data?.data?.categories || [];

        let dynamicTabs: { label: string; icon: string; category: string }[] = [];

        if (on_Prem_Tab_Selected === 2) {
          // Only Benchmark - use isBenchVersions
          if (isBenchVersions && isBenchVersions.length > 0) {
            dynamicTabs = isBenchVersions.map((benchmark: string) => {
              const label = benchmark;
              const icon = iconMap[label.toUpperCase()] || CIS_img;
              return { label, icon, category: "Benchmark" };
            });
          } else {
            if (resolvedModule === "cloud" && controlType) {
              // For Cloud Details tab, use the row's control_type directly (e.g. only CIS or only NIST)
              const controlTypes = Array.isArray(controlType)
                ? controlType
                : String(controlType)
                    .split(",")
                    .map((item: string) => item.trim())
                    .filter(Boolean);

              dynamicTabs = controlTypes.map((ctrl: string) => {
                const label = ctrl.toUpperCase();
                const icon = iconMap[label] || CIS_img;
                return { label, icon };
              });
            } else {
              const benchmark = categories.find(
                (cat: any) => cat.category_name === "Benchmark"
              );
              if (benchmark?.controls) {
                dynamicTabs = benchmark.controls.map((control: any) => {
                  const label = control.control_display_name;
                  const icon = iconMap[label.toUpperCase()] || CIS_img;
                  return { label, icon };
                });
              }
            }
          }
        } else if (on_Prem_Tab_Selected === 1) {
          // Framework, Benchmark (CIS lives here, not Framework), and MITRE (if present)
          const framework = categories.find(
            (cat: any) => cat.category_name === "Framework"
          );
          const benchmark = categories.find(
            (cat: any) => cat.category_name === "Benchmark"
          );
          const mitre = categories.find(
            (cat: any) => cat.category_name === "MITRE"
          );
          if (
            (framework?.controls && framework.controls.length > 0) ||
            (benchmark?.controls && benchmark.controls.length > 0)
          ) {
            [framework, benchmark].forEach((cat: any) => {
              if (cat?.controls) {
                dynamicTabs = dynamicTabs.concat(
                  cat.controls.map((control: any) => {
                    const label = control.control_display_name;
                    const icon = iconMap[label.toUpperCase()] || CIS_img;
                    return { label, icon, category: cat.category_name };
                  })
                );
              }
            });
          } else {
            categories.forEach((cat: any) => {
              if (cat.controls) {
                cat.controls.forEach((control: any) => {
                  const label = control.control_display_name;
                  if (label && !dynamicTabs.some((t) => t.label === label)) {
                    const icon = iconMap[label.toUpperCase()] || CIS_img;
                    dynamicTabs.push({ label, icon, category: cat.category_name });
                  }
                });
              }
            });
          }
          if (mitre?.controls) {
            dynamicTabs = dynamicTabs.concat(
              mitre.controls.map((control: any) => {
                const label = control.control_display_name;
                const icon = iconMap[label.toUpperCase()] || CIS_img;
                return { label, icon, category: "MITRE" };
              })
            );
          }
        }

        // Deduplicate tabs to prevent things like MITRE being added twice
        const uniqueTabs = dynamicTabs.filter(
          (tab, index, self) =>
            index === self.findIndex((t) => t.label.toUpperCase() === tab.label.toUpperCase())
        );

        setTabsData(uniqueTabs);
        setIsReady(true);
      } catch (error) {
        console.error("Error fetching compliance list:", error);
        setTabsData([]);
      } finally {
        setIsLoading(false);
        setLoading(false);
      }
    };

    fetchButtons();
  }, [on_Prem_Tab_Selected, Selected_Rowid, isBenchVersions, resolvedModule, controlType, setLoading]);

  // Reset isReady when dependencies change
  useEffect(() => {
    if (isTemplate) {
      setTabsData([{ label: "Template", icon: customTemplateLogo }]);
      setIsReady(true);
      setIsLoading(false);
      return;
    }
    setIsReady(false);
  }, [on_Prem_Tab_Selected, Selected_Rowid, isTemplate]);

  useEffect(() => {
    setSelectedTab(0);
    if (tabsData.length > 0) {
      dispatch(setButtonSelected(tabsData[0].label.toLowerCase()));
      dispatch(setCategorySelected(tabsData[0].category));
    }
  }, [on_Prem_Tab_Selected, tabsData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    dispatch(setButtonSelected(tabsData[newValue].label.toLowerCase()));
    dispatch(setCategorySelected(tabsData[newValue].category));
  };

  const getButtonWidth = (count: number) => {
    const widthMap: Record<number, number> = {
      1: 130,
      2: 210,
      3: 310,
      4: 420,
    };
    return `${widthMap[count] || 520}px`;
  };

  const ButtonWidth = getButtonWidth(tabsData.length);

  if (!isReady || isLoading || tabsData.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: isDarkMode ? "#191C24" : "#F1F1FF",
          p: 1,
          width: "200px",
          height: "60px",
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <SkeletonLoader count={1} styles={{ height: 40, width: "100%" }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={isTemplate ? 0 : 3}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: isDarkMode ? "#191C24" : "#F1F1FF",
        p: 0.5,
        maxWidth: ButtonWidth,
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        position: "sticky",
        ...(isTemplate && {
          boxShadow: isDarkMode
            ? "0 1px 4px rgba(0, 0, 0, 0.25)"
            : "0 1px 4px rgba(58, 42, 218, 0.12)",
        }),
      }}
    >
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          position: "relative",
          "& .MuiTabs-indicator": {
            height: "100%",
            backgroundColor: isDarkMode ? "#343842" : "white",
            // backgroundColor: "white",
            borderRadius: "8px",
            transition: "all 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {tabsData.map((tab, index) => (
          <Tab
            key={index}
            label={
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1}
                position="relative"
                zIndex={2}
              >
                <img
                  src={tab.icon}
                  alt={tab.label}
                  width={85}
                  height={25}
                  style={{ cursor: tabsData.length > 1 ? "pointer" : "default" }}
                />
              </Box>
            }
            sx={{
              textTransform: "none",
              color: selectedTab === index ? "black" : "#555",
              transition: "color 0.3s ease-in-out",
              minWidth: "100px",
              maxWidth: "150px",
            }}
          />
        ))}
      </Tabs>
    </Paper>
  );
};

export default TabSelection;